import { NextResponse } from "next/server"
import {
  type DokanStoreReview,
  getStoreReviewsPage,
} from "@/services/dokan-service"

const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 25
const MAX_PER_PAGE = 100

const parsePositiveInt = (value: string | null, fallback: number): number => {
  if (!value) {
    return fallback
  }

  const parsedValue = Number.parseInt(value, 10)
  if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
    return fallback
  }

  return parsedValue
}

const toRating = (review: DokanStoreReview): number =>
  Math.min(5, Math.max(0, Math.round(review.rating)))

const buildDistribution = (reviews: DokanStoreReview[], total: number) =>
  [5, 4, 3, 2, 1].map((star) => {
    const count = reviews.filter((review) => toRating(review) === star).length

    return {
      star,
      count,
      percent: total > 0 ? Math.round((count / total) * 100) : 0,
    }
  })

const buildEmptyResponse = (page: number, perPage: number) => ({
  reviews: [],
  page,
  perPage,
  totalPages: 1,
  totalReviews: 0,
  summary: {
    average: 0,
    distribution: buildDistribution([], 0),
  },
})

export async function GET(request: Request): Promise<NextResponse> {
  const { searchParams } = new URL(request.url)
  const page = parsePositiveInt(searchParams.get("page"), DEFAULT_PAGE)
  const perPage = Math.min(
    MAX_PER_PAGE,
    parsePositiveInt(searchParams.get("per_page"), DEFAULT_PER_PAGE)
  )
  const storeId = parsePositiveInt(searchParams.get("store_id"), 0)

  if (storeId === 0) {
    return NextResponse.json(buildEmptyResponse(page, perPage))
  }

  const { reviews, totalPages, totalReviews } = await getStoreReviewsPage({
    storeId,
    page,
    perPage,
  })

  const average =
    reviews.length > 0
      ? reviews.reduce((total, review) => total + toRating(review), 0) /
        reviews.length
      : 0

  return NextResponse.json({
    reviews,
    page,
    perPage,
    totalPages,
    totalReviews,
    summary: {
      average,
      distribution: buildDistribution(reviews, reviews.length),
    },
  })
}
